'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const React = require('react');
const react = require('@emotion/react');
const DayPicker = require('react-day-picker');
const hooks_useClickOutside = require('../../../hooks/useClickOutside.cjs.js');
require('../../../theme/Colors.cjs.js');
require('../../../theme/Shadows.cjs.js');
require('../../../theme/Typography.cjs.js');
const theme_Zindexes = require('../../../theme/Zindexes.cjs.js');
const components_navigation_Button_Button = require('../../navigation/Button/Button.cjs.js');
const components_navigation_ButtonNav_ButtonNav = require('../../navigation/ButtonNav/ButtonNav.cjs.js');
const components_display_FloatingBox_FloatingBox = require('../../display/FloatingBox/FloatingBox.cjs.js');
const components_forms_DateRangePicker_DateRangePickerInput = require('./DateRangePickerInput.cjs.js');
const components_forms_DateRangePicker_DateRangePickerNavbar = require('./DateRangePickerNavbar.cjs.js');
const components_forms_DateRangePicker_WithDayPickerStyle = require('./WithDayPickerStyle.cjs.js');
const components_forms_DateRangePicker_utils = require('./utils.cjs.js');
const styleEncapsulation = require('@zapier/style-encapsulation');

const _interopDefault = e => e && e.__esModule ? e : { default: e };

const DayPicker__default = /*#__PURE__*/_interopDefault(DayPicker);

const Styles = styleEncapsulation.labelStyles("DateRangePicker", {
  root: react.css`
    position: relative;
  `,
  menu: (props) => [
    react.css`
      display: grid;
      gap: var(--zds-space-20);
      padding: var(--zds-space-20);
    `,
    props.size === "large" && react.css`
        grid-template-columns: auto 1fr;
      `
  ],
  options: (props) => [
    react.css`
      display: flex;
      flex-direction: column;
      gap: var(--zds-space-12);
    `,
    props.size === "large" && react.css`
        border-right: var(--zds-border-weak);
        padding-right: var(--zds-space-20);
        min-width: 195px;
      `
  ],
  doneButton: react.css`
    display: flex;
    justify-content: flex-end;
    grid-column: 1 / -1;
  `,
  modalBackdrop: react.css`
    background: rgba(0, 0, 0, 0.8);
    position: fixed;
    z-index: ${theme_Zindexes.Zindexes.modalOverlay};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  modal: (size) => [
    react.css`
      background: var(--zds-background-weaker);
      border-radius: var(--zds-radius-large);
      overflow: auto;
      width: 100%;
      max-width: 344px;
      max-height: 100vh;
    `,
    size === "small" && react.css({
      maxWidth: "344px"
    }),
    size === "medium" && react.css({
      maxWidth: "670px"
    })
  ]
});
const defaultValue = {};
const DateRangePicker = ({ "aria-label": ariaLabel, menuAriaLabel, onChange, onDayClick = () => {
}, value: valueProp = defaultValue, placeholder = void 0, disabledDates = void 0, id = void 0, isDisabled = void 0, isErrored = void 0, closeText = "Done", options = [], menuAlign = "left", size = "medium", inputSize = "medium", variant: variantProp = void 0 }) => {
  const props = {
    "aria-label": ariaLabel,
    menuAriaLabel,
    onChange,
    onDayClick,
    value: valueProp,
    placeholder,
    disabledDates,
    id,
    isDisabled,
    isErrored,
    closeText,
    options,
    menuAlign,
    size
  };
  let variant = variantProp;
  if (!variant) {
    if (size === "small") {
      variant = "modal";
    } else {
      variant = "dropdown";
    }
  }
  const [selectedOption, setSelectedOption] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [value, setValue] = React.useState(props.value || defaultValue);
  const { from, to } = value;
  const clampedSize = props.size === "small" ? props.size : "medium";
  const isSmall = props.size === "small";
  React.useEffect(() => {
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);
  const clearSelection = () => {
    setValue(defaultValue);
    props.onChange(defaultValue);
  };
  const closeMenu = () => {
    var _a, _b;
    setTimeout(() => {
      setIsOpen(false);
    }, 0);
    if (!components_forms_DateRangePicker_utils.isSameDay(from, (_a = props.value) === null || _a === void 0 ? void 0 : _a.from) || !components_forms_DateRangePicker_utils.isSameDay(to, (_b = props.value) === null || _b === void 0 ? void 0 : _b.to)) {
      props.onChange(value);
    }
  };
  const openMenu = () => {
    setIsOpen(true);
  };
  const handleDayClick = (day, modifiers, classNames) => {
    onDayClick(day);
    const disabledModifier = classNames.disabled;
    if (modifiers[disabledModifier]) {
      return;
    }
    const nextRange = components_forms_DateRangePicker_utils.calculateNextRange(day, from, to);
    setValue(nextRange);
    if (selectedOption !== null) {
      setSelectedOption(null);
    }
  };
  const handleSelectOption = (option, event) => {
    event.preventDefault();
    setSelectedOption(option);
    setValue(option.value);
  };
  const [setClickOutsideNode] = hooks_useClickOutside.useClickOutside({
    onClickOutside: closeMenu
  });
  const menuInner = isOpen && react.jsx(
    "div",
    { "aria-label": props.menuAriaLabel, css: Styles.menu(props), "data-zds": true },
    props.options.length > 0 && react.jsx("div", { css: Styles.options(props), "data-zds": true }, props.options.map((option) => react.jsx(components_navigation_ButtonNav_ButtonNav.ButtonNav, { "aria-label": "Predefined date ranges", isButtonFullWidth: true, items: [option], onChooseItem: handleSelectOption, selectedItemKey: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.key }))),
    react.jsx(components_forms_DateRangePicker_WithDayPickerStyle.WithDayPickerStyle, { size: clampedSize }, ({ classNames, modifiersStyles }) => react.jsx(DayPicker__default.default, { classNames, disabledDays: props.disabledDates, fixedWeeks: true, initialMonth: from, modifiers: value, modifiersStyles, navbarElement: components_forms_DateRangePicker_DateRangePickerNavbar.DateRangePickerNavbar, numberOfMonths: isSmall ? 1 : 2, onDayClick: (day, modifiers) => handleDayClick(day, modifiers, classNames), selectedDays: [from, value] })),
    react.jsx(
      "div",
      { css: Styles.doneButton, "data-zds": true },
      react.jsx(components_navigation_Button_Button.Button, { isFullWidth: props.size === "small", onClick: closeMenu }, props.closeText)
    )
  );
  return react.jsx(
    "div",
    { css: Styles.root, "data-zds": true },
    react.jsx(components_forms_DateRangePicker_DateRangePickerInput.DateRangePickerInput, Object.assign({}, props, { clearSelection, isOpen, onClick: () => {
      if (!props.isDisabled && !isOpen) {
        openMenu();
      }
    }, options: props.options, value, size: inputSize })),
    isOpen && (variant === "modal" ? react.jsx(
      "div",
      { css: Styles.modalBackdrop, "data-zds": true },
      react.jsx("div", { css: Styles.modal(clampedSize), ref: setClickOutsideNode, "data-zds": true }, menuInner)
    ) : react.jsx(components_display_FloatingBox_FloatingBox.FloatingBox, { align: props.menuAlign, borderRadius: "large", intersectHeight: 460, onClickOutside: closeMenu }, menuInner))
  );
};

exports.DateRangePicker = DateRangePicker;
//# sourceMappingURL=DateRangePicker.cjs.js.map
